import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { formatTime } from '@easyedit/timeline'
import { api } from '../../lib/api/client'
import type { ChangeSetResponse } from '../../lib/api/types'
import { useEditorStore } from '../../stores/editorStore'

/**
 * Revisão das alterações propostas pela IA.
 *
 * É a tese do produto virando interface: a IA fez o primeiro corte, e aqui a pessoa
 * decide o que fica. Cada alteração traz o motivo em português e pode ser recusada
 * individualmente.
 */
export function ChangeReview({
  changeSet,
  onResolved,
}: {
  changeSet: ChangeSetResponse
  onResolved: () => void
}) {
  const changes = changeSet.preview ?? []
  const [accepted, setAccepted] = useState<Set<string>>(() => new Set(changes.map((c) => c.id)))
  const setPlayhead = useEditorStore((s) => s.setPlayhead)

  const accept = useMutation({
    mutationFn: () =>
      api.post(`/changesets/${changeSet.changeSetId}/accept`, { changeIds: [...accepted] }),
    onSuccess: onResolved,
  })
  const reject = useMutation({
    mutationFn: () => api.post(`/changesets/${changeSet.changeSetId}/reject`, {}),
    onSuccess: onResolved,
  })

  const removedSeconds = useMemo(
    () =>
      changes
        .filter((c) => accepted.has(c.id) && c.start !== null && c.end !== null)
        .reduce((sum, c) => sum + ((c.end ?? 0) - (c.start ?? 0)), 0),
    [changes, accepted],
  )

  function toggle(id: string) {
    setAccepted((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="rounded-lg border border-accent/40 bg-ink-900 overflow-hidden">
      <div className="px-3 py-2.5 border-b border-ink-750 bg-accent-soft/30">
        <p className="text-2xs text-ink-100 whitespace-pre-line leading-relaxed">{changeSet.summary}</p>
        {changeSet.breakdown && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {Object.entries(changeSet.breakdown)
              .filter(([, n]) => n > 0)
              .map(([label, n]) => (
                <span key={label} className="text-[9px] rounded bg-ink-800 px-1.5 py-0.5 text-ink-300">
                  {n} {label}
                </span>
              ))}
          </div>
        )}
      </div>

      <div className="max-h-56 overflow-y-auto divide-y divide-ink-800">
        {changes.map((change) => {
          const on = accepted.has(change.id)
          return (
            <div
              key={change.id}
              className={`px-3 py-2 flex items-start gap-2 transition-colors ${on ? '' : 'opacity-45'}`}
            >
              <input
                id={`change-${change.id}`}
                type="checkbox"
                checked={on}
                onChange={() => toggle(change.id)}
                className="mt-0.5 accent-accent shrink-0"
                aria-label={change.reason}
              />
              <div className="min-w-0 flex-1">
                <p className="text-2xs text-ink-200 leading-snug">{change.reason}</p>
                {change.start !== null && (
                  <button
                    className="mt-0.5 font-mono text-[9px] text-accent hover:underline tnum"
                    onClick={() => setPlayhead(change.start!)}
                    title="Ir para este ponto"
                  >
                    {formatTime(change.start, false)}
                    {change.end !== null ? ` → ${formatTime(change.end, false)}` : ''}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {changeSet.rejected && changeSet.rejected.length > 0 && (
        <div className="px-3 py-2 border-t border-ink-800 bg-danger-soft/20">
          <p className="text-[9px] text-danger">
            {changeSet.rejected.length} operações foram descartadas por não serem válidas nesta timeline.
          </p>
        </div>
      )}

      <div className="px-3 py-2.5 border-t border-ink-750 flex items-center gap-2">
        <span className="text-[9px] text-ink-400 tnum flex-1">
          {accepted.size} de {changes.length}
          {removedSeconds > 0 && ` · −${removedSeconds.toFixed(1)}s`}
        </span>
        <button className="btn-ghost" onClick={() => reject.mutate()} disabled={reject.isPending}>
          Descartar
        </button>
        <button
          className="btn-primary"
          onClick={() => accept.mutate()}
          disabled={accept.isPending || accepted.size === 0}
        >
          {accept.isPending ? 'Aplicando…' : `Aplicar ${accepted.size}`}
        </button>
      </div>
    </div>
  )
}
